import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import CustodianPicker from './CustodianPicker';
import { useToast } from '../../context/ToastContext';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { CostCentreService } from '../../services/costCentre.service';
import { SegmentService } from '../../services/segment.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { DepartmentOption } from '../../interfaces/costCentre.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { FixedAsset, assetCarrying } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { formatDate, formatMonthYear, toLocalIsoDate } from '../../utils/dateUtils';

interface Props {
  open: boolean;
  asset: FixedAsset;
  /** The caller also holds FIXED_ASSET_MANAGE: needed to move the asset to another segment. */
  canChangeSegment: boolean;
  onClose: () => void;
  onDone: () => void;
}

/** First day of the month after `depreciatedThrough` (YYYY-MM-DD), or null when nothing has depreciated. */
export const earliestSegmentChangeDate = (depreciatedThrough?: string | null): string | null => {
  if (!depreciatedThrough) return null;
  const [y, m] = depreciatedThrough.split('-').map(Number);
  const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
  return `${next.y}-${String(next.m).padStart(2, '0')}-01`;
};

/**
 * H1: the first month this asset still owes depreciation for (YYYY-MM-01), or null when none is owed or the
 * client cannot tell (a registered-existing asset with no marker yet: the server applies its opening floor and
 * enforces the rule itself). Depreciation reads the segment live, so a segment change must not leave owed months
 * behind it.
 */
export const firstOwedMonth = (asset: Pick<FixedAsset, 'status' | 'depreciatedThrough' | 'depreciationStartDate'
  | 'acquisitionSource' | 'acquisitionCost' | 'accumulatedDepreciation' | 'carryingAmount' | 'salvageValue'>): string | null => {
  if (asset.status !== 'ACTIVE' || !asset.depreciationStartDate) return null;
  if (assetCarrying(asset) <= (asset.salvageValue ?? 0)) return null;   // nothing left to charge
  if (asset.depreciatedThrough) return earliestSegmentChangeDate(asset.depreciatedThrough);
  if (asset.acquisitionSource === 'REGISTERED_EXISTING') return null;
  return `${asset.depreciationStartDate.slice(0, 7)}-01`;
};

/** The month before `monthStart` as YYYY-MM. */
const previousMonth = (monthStart: string): string => {
  const [y, m] = monthStart.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
};

/**
 * F10 transfer: the form holds the asset's placement AFTER the move, prefilled with where it is now.
 * Location / department / custodian changes post nothing; a segment change posts an FA_SEGMENT_TRANSFER
 * reclass journal and needs FIXED_ASSET_MANAGE as well.
 */
const AssetTransferDialog: React.FC<Props> = ({ open, asset, canChangeSegment, onClose, onDone }) => {
  const toast = useToast();
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [transferDate, setTransferDate] = useState(toLocalIsoDate());
  const [locationId, setLocationId] = useState(asset.locationId ?? '');
  const [departmentId, setDepartmentId] = useState(asset.departmentId ?? '');
  const [custodianUserId, setCustodianUserId] = useState(asset.custodianUserId ?? '');
  const [segmentId, setSegmentId] = useState(asset.segmentId ?? '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTransferDate(toLocalIsoDate());
    setLocationId(asset.locationId ?? '');
    setDepartmentId(asset.departmentId ?? '');
    setCustodianUserId(asset.custodianUserId ?? '');
    setSegmentId(asset.segmentId ?? '');
    setReason('');
    // A failed list is reported, not swallowed; segments load only for a caller who may change them.
    const soft = <T,>(p: Promise<T>, fallback: T, what: string): Promise<T> =>
      p.catch(e => { toast.showApiError(e, `Failed to load ${what}`); return fallback; });
    Promise.all([
      soft(FixedAssetTransferService.locations(), [] as AssetLocation[], 'asset locations'),
      soft(CostCentreService.getDepartments(), [] as DepartmentOption[], 'departments'),
      canChangeSegment
        ? soft(SegmentService.getByInstitution(0, 200), { content: [] as Segment[] } as { content: Segment[] }, 'segments')
        : Promise.resolve({ content: [] as Segment[] }),
    ]).then(([locs, depts, segs]) => {
      setLocations(Array.isArray(locs) ? locs : []);
      setDepartments(Array.isArray(depts) ? depts : []);
      setSegments(segs?.content ?? []);
    });
  }, [open, asset, canChangeSegment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deactivated locations are not offered as a target, except the asset's current one so the box is not blank.
  const locationOptions = useMemo(
    () => locations.filter(l => l.active || l.id === asset.locationId), [locations, asset.locationId]);
  const segmentOptions = useMemo(
    () => segments.filter(s => s.active !== false || s.id === asset.segmentId), [segments, asset.segmentId]);

  const segmentChanges = (segmentId || '') !== (asset.segmentId || '');
  const nothingChanges = !segmentChanges
    && (locationId || '') === (asset.locationId || '')
    && (departmentId || '') === (asset.departmentId || '')
    && (custodianUserId || '') === (asset.custodianUserId || '');
  const earliest = earliestSegmentChangeDate(asset.depreciatedThrough);
  const tooEarly = segmentChanges && !!earliest && !!transferDate && transferDate < earliest;
  // H1: months owed before the transfer month would post to the NEW segment, so run depreciation first
  const owed = firstOwedMonth(asset);
  const transferMonth = transferDate ? `${transferDate.slice(0, 7)}-01` : '';
  const depreciationBehind = segmentChanges && !tooEarly && !!owed && !!transferMonth && owed < transferMonth;
  const future = !!transferDate && transferDate > toLocalIsoDate();
  const invalid = !transferDate || nothingChanges || tooEarly || depreciationBehind || future;

  const submit = async () => {
    try {
      setSaving(true);
      const t = await FixedAssetTransferService.transfer(asset.id, {
        transferDate,
        toLocationId: locationId || undefined,
        toDepartmentId: departmentId || undefined,
        toCustodianUserId: custodianUserId || undefined,
        toSegmentId: segmentId || undefined,
        reason: reason.trim() || undefined,
      });
      toast.showSuccess(t.journalEntryId ? `Transfer ${t.transferNo} recorded and reclass posted`
        : `Transfer ${t.transferNo} recorded`);
      onDone();
    } catch (e) {
      toast.showApiError(e, 'Failed to transfer asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="fixedAssets.transferDialog.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Transfer {asset.assetCode}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField data-help="fixedAssets.transferDialog.field.date" label="Transfer date" type="date" required value={transferDate}
            onChange={e => setTransferDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth
            error={tooEarly || future || depreciationBehind}
            helperText={future ? 'A transfer cannot be dated in the future.'
              : tooEarly ? `Depreciation is posted through ${formatMonthYear(asset.depreciatedThrough!)}; date the segment change on or after ${formatDate(earliest!)}.`
                : depreciationBehind ? `Depreciation is not posted from ${owed!.slice(0, 7)}; run depreciation through ${previousMonth(transferMonth)} first, then make the segment transfer.`
                  : undefined} />
          <SearchableSelect<AssetLocation> data-help="fixedAssets.transferDialog.field.location"
            label="Location" options={locationOptions} value={locationId}
            onChange={id => setLocationId(id)} getOptionLabel={l => `${l.code} - ${l.name}${l.active ? '' : ' (inactive)'}`}
            getOptionId={l => l.id} data-testid="transfer-location" />
          <SearchableSelect<DepartmentOption> data-help="fixedAssets.transferDialog.field.department"
            label="Department" options={departments} value={departmentId}
            onChange={id => setDepartmentId(id)} getOptionLabel={d => d.name} getOptionId={d => d.id}
            selectedOption={asset.departmentId && asset.departmentName
              ? { id: asset.departmentId, name: asset.departmentName, costCentreCode: null, unitType: null } : undefined} />
          <CustodianPicker data-help="fixedAssets.transferDialog.field.custodian" value={custodianUserId} valueLabel={asset.custodianName}
            onChange={id => setCustodianUserId(id)} data-testid="transfer-custodian" />
          {canChangeSegment && (
            <SearchableSelect<Segment> data-help="fixedAssets.transferDialog.field.segment"
              label="Segment" options={segmentOptions} value={segmentId}
              onChange={id => setSegmentId(id)} getOptionLabel={s => `${s.code} - ${s.name}`} getOptionId={s => s.id ?? ''}
              placeholder="Common (none)" data-testid="transfer-segment" />
          )}
          {segmentChanges && (
            <Alert severity="info" data-testid="segment-change-note">
              Changing the segment posts a reclass journal (FA_SEGMENT_TRANSFER) that moves the asset's cost,
              accumulated depreciation, impairment and revaluation reserve to the new segment. Depreciation
              from the next run is charged to the new segment.
            </Alert>
          )}
          <TextField data-help="fixedAssets.transferDialog.field.reason" label="Reason" value={reason} onChange={e => setReason(e.target.value)}
            fullWidth multiline minRows={2} inputProps={{ maxLength: 500 }} />
          {nothingChanges && (
            <Alert severity="warning">
              Change at least one of location, department, custodian{canChangeSegment ? ' or segment' : ''}.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.transferDialog.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fixedAssets.transferDialog.save" variant="contained" onClick={submit} disabled={invalid || saving}>Transfer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetTransferDialog;
